import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';

import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { StatusFilterComponent } from '@app/shared/components/status-filter/status-filter.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  POS_SHIFT_CLOSE_COLUMNS,
  POS_SHIFT_CLOSE_DEFAULT_COLUMNS_ORDER,
  POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS_ORDER,
  POS_SHIFT_CLOSE_ACTION_COLUMN,
  getPosShiftCloseSearchConfig,
} from './pos-shift-close.config';
import {
  PosShiftCloseService,
  PosShiftClosing,
  PosShiftClosingSearchParams,
} from './services/pos-shift-close.service';

@Component({
  selector: 'app-pos-shift-close',
  standalone: true,
  imports: [
    CommonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
  ],
  providers: [MessageService],
  templateUrl: './pos-shift-close.component.html',
  styleUrls: ['./pos-shift-close.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosShiftCloseComponent implements OnInit {
  // Search configuration
  searchConfig!: FormFieldConfig[];

  // Table configuration (kept for reference, but using advanced table columns below)
  tableColumns = POS_SHIFT_CLOSE_COLUMNS;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using specific submenu 'close_pos_shift' for POS permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('pos.close_pos_shift.view');
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(() => this.canRead());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_SHIFT_CLOSE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of POS_SHIFT_CLOSE_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(POS_SHIFT_CLOSE_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      POS_SHIFT_CLOSE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of POS_SHIFT_CLOSE_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Data signals with proper typing
  posShiftClosings = signal<PosShiftClosing[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal({
    posId: undefined,
    shopId: undefined,
    cashierId: undefined,
    cashierName: undefined,
    startTimeFrom: undefined,
    startTimeTo: undefined,
    status: undefined,
    page: 0,
    size: 20,
    sortBy: 'endTime', // Default sort by endTime
    ascending: false, // Default to descending (latest first)
  });

  // Selection with proper typing
  selectedPosShiftClosings = signal<PosShiftClosing[]>([]);

  // Computed properties
  hasSelected = computed(() => this.selectedPosShiftClosings().length > 0);

  constructor(
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly posShiftCloseService: PosShiftCloseService,
  ) {
    this.searchConfig = withPaginatedSearchFields(
      getPosShiftCloseSearchConfig(this.posShiftCloseService),
      {
        posId: {
          loadPage: (params) =>
            this.posShiftCloseService.getPosTerminalsOptionsPage(params),
        },
      },
    );
  }

  ngOnInit(): void {
    this.loadPosShiftClosings();
  }

  /**
   * Load POS shift closings based on search parameters
   */
  loadPosShiftClosings(): void {
    this.loading.set(true);

    const searchParams: PosShiftClosingSearchParams = {
      posId: this.searchParams().posId,
      shopId: this.searchParams().shopId,
      cashierId: this.searchParams().cashierId,
      name: this.searchParams().cashierName,
      startTimeFrom: this.searchParams().startTimeFrom,
      startTimeTo: this.searchParams().startTimeTo,
      status: this.searchParams().status,
      page: this.searchParams().page,
      size: this.searchParams().size,
      sortBy: this.searchParams().sortBy,
      ascending: this.searchParams().ascending,
    };

    this.posShiftCloseService.getPosShiftClosings(searchParams).subscribe({
      next: (response) => {
        this.posShiftClosings.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading POS shift closings:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'pos.shift_close.messages.load_failed',
          ),
        });
      },
    });
  }

  /**
   * Handle search form submission
   */
  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 0, // Reset to page 0 when searching
      size: this.searchParams().size, // Keep current size
    });
    this.loadPosShiftClosings();
  }

  /**
   * Handle search form reset
   */
  onReset(): void {
    this.searchParams.set({
      posId: undefined,
      shopId: undefined,
      cashierId: undefined,
      cashierName: undefined,
      startTimeFrom: undefined,
      startTimeTo: undefined,
      status: undefined,
      page: 0,
      size: 20,
      sortBy: 'endTime', // Reset to default sorting
      ascending: false, // Reset to default direction
    });
    this.selectedPosShiftClosings.set([]);
    this.loadPosShiftClosings();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(
        'pos.shift_close.messages.search_reset',
      ),
    });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page,
    });
    this.loadPosShiftClosings();
  }

  /**
   * Navigate to view POS shift closing details
   */
  onView(shiftClosing: PosShiftClosing): void {
    this.router.navigate(['/pos/shift-close/view', shiftClosing.id]);
  }

  /**
   * Get status icon class
   */
  getStatusIcon(status: string): string {
    switch (status) {
      case 'CLOSED':
        return 'pi pi-check-circle';
      case 'PENDING':
        return 'pi pi-clock';
      case 'OPEN':
        return 'pi pi-play-circle';
      default:
        return 'pi pi-question-circle';
    }
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: string): string {
    return this.translateService.instant(
      `pos.shift_close.status.${status.toLowerCase()}`,
    );
  }

  /**
   * Get status severity
   */
  getStatusSeverity(status: string): string {
    switch (status) {
      case 'CLOSED':
        return 'danger';
      case 'PENDING':
        return 'warning';
      case 'OPEN':
        return 'success';
      default:
        return 'info';
    }
  }

  /**
   * Get status style
   */
  getStatusStyle(status: string): any {
    const colorMap: { [key: string]: string } = {
      CLOSED: '#22c55e',
      PENDING: '#f59e0b',
      OPEN: '#3b82f6',
    };

    return {
      '--status-color': colorMap[status] || '#6b7280',
    };
  }

  /**
   * Format currency using utility function
   */
  formatCurrency = formatCurrencyVND;

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
